import type {AgentProfile, ChatInputSlotProps, LeadingIconSlotProps} from '../agent-profile'
import type {TrailingVisualSlotProps} from '../agent-profile-types'
import {AGENT_INFO_REGISTRY} from '@github-ui/copilot-shared-ui/agents/agent-info-registry'
import {VscodeSessionIcon} from '../../components/VscodeSessionIcon'
import {EmptyCliSession} from '../../components/session-logs/EmptyCliSession'
import {CLIChatInput} from '../../components/task-view/CLIChatInput'
import {computeConnectionStatus} from '../../utils/connection-status'
import {ConnectionStatus} from '../../types/session'
import type {SessionState} from '../../types/session'
import {REMOTABLE_BASE} from './remotable-base'
import {VscodeIcon} from '@primer/octicons-react'
import trailingStyles from '../../components/AgentTypeTrailingVisual.module.css'

function VSCodeLeadingIcon({staleAt, dataUpdatedAt, state}: LeadingIconSlotProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, state as SessionState, dataUpdatedAt)
  const isConnected = connectionStatus === ConnectionStatus.Connected

  return <VscodeSessionIcon showConnectedDot={isConnected} />
}

function RemotableChatInputSlot({
  session,
  task,
  textAreaRef,
  onCommandSent,
  cliRemoteNoticeDismissed,
  remoteControlPolicyEnabled,
  ssoOrgIds,
}: ChatInputSlotProps) {
  const remoteSteerable = task?.remote_steerable === true
  const agentTaskId = task?.agent_collaborators?.[0]?.agent_task_id
  return (
    <CLIChatInput
      session={session}
      textAreaRef={textAreaRef}
      remoteSteerable={remoteSteerable}
      remoteControlPolicyEnabled={remoteControlPolicyEnabled}
      ssoOrgIds={ssoOrgIds}
      agentTaskId={agentTaskId}
      staleAt={task?.stale_at}
      onCommandSent={onCommandSent}
      cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
    />
  )
}

function VSCodeTrailingVisual({state, staleAt, dataUpdatedAt}: TrailingVisualSlotProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, state, dataUpdatedAt)
  const isConnected = connectionStatus === ConnectionStatus.Connected
  const label = isConnected ? 'VS Code session connected' : 'VS Code session'

  return (
    <span
      className={trailingStyles.wrapper}
      role="img"
      aria-label={label}
      data-testid="agent-type-trailing-visual-vscode"
    >
      <VscodeIcon />
      {isConnected && <span data-testid="connected-dot" className={trailingStyles.connectedDot} aria-hidden="true" />}
    </span>
  )
}

/**
 * Profile overrides for the VS Code Chat agent.
 * Extends the remotable base with VS Code-specific icon and identity.
 */
export const VSCODE_PROFILE: Partial<AgentProfile> = {
  ...REMOTABLE_BASE,
  slug: 'vscode-chat',
  displayName: 'Copilot in VS Code',
  secondaryLabel: 'VS Code',

  agentInfo: AGENT_INFO_REGISTRY['vscode-chat'],

  slots: {
    LeadingIcon: VSCodeLeadingIcon,
    ChatInput: RemotableChatInputSlot,
    EmptyState: EmptyCliSession,
    TrailingVisual: VSCodeTrailingVisual,
  },
}
